// E-Invoice validation policy wizard (BOL-440 frontend).
// Mirrors the GET / POST contract on doc2-api
// routers/electronic_document/router.py — endpoints registered
// under /electronic_document/policy/wizard/*.
//
// Spec: docs/research/xrechnung_validation_policy_structure.md §4

import AbstractApiService from 'src/services/abstractApiService';
import { api } from 'pages/APIs';

// ── Wire types ────────────────────────────────────────────────────

/** One of the 5 quick-setup profiles defined in profiles.py. */
export type WizardProfileId =
  | 'b2g_public_sector'
  | 'b2b_domestic'
  | 'b2b_eu_cross_border'
  | 'migration_testing'
  | 'custom';

/** Six business categories every rule maps to (rule_categories.py). */
export type RuleCategory =
  | 'document_format'
  | 'invoice_structure'
  | 'tax_and_vat'
  | 'banking_and_payment'
  | 'identification'
  | 'pricing_and_dates';

/** Severity-direction relative to KoSIT defaults. */
export type Preset = 'strict' | 'standard' | 'lax';

/** Question grouping for the Tier-2 wizard step layout. */
export type QuestionGroup =
  | 'doc_format'
  | 'banking'
  | 'tax'
  | 'identification'
  | 'behavior';

export type AnswerType =
  | 'yes_no'
  | 'single_choice'
  | 'multi_select'
  | 'tolerance';

export interface ProfileDef {
  id: WizardProfileId;
  label_key: string;
  description_key: string;
  category_presets: Record<RuleCategory, Preset>;
  accepted_versions: string[];
  accepted_profiles: string[];
  vies_enabled: boolean;
  blocking_enabled: boolean;
  supplier_notification_enabled: boolean;
  is_default: boolean;
}

export interface QuestionDef {
  id: string; // 'A1', 'B3', etc.
  group: QuestionGroup;
  label_key: string;
  help_key: string | null;
  answer_type: AnswerType;
  options: string[];
  default: string | string[];
  sort_order: number;
}

export interface DefinitionsResponse {
  success: boolean;
  profiles: ProfileDef[];
  questions: QuestionDef[];
}

export interface ResolvedPolicy {
  profile: WizardProfileId;
  category_presets: Record<RuleCategory, Preset>;
  rule_overrides: Record<string, string>;
  accepted_versions: string[];
  accepted_profiles: string[];
  vies_enabled: boolean;
  blocking_enabled: boolean;
  supplier_notification_enabled: boolean;
}

export interface StateResponse {
  success: boolean;
  doc_type: string;
  sub_doc_type: string | null;
  profile: WizardProfileId;
  answers: Record<string, string | string[]>;
  resolved: ResolvedPolicy;
  /** True iff the wizard was ever saved on this doc-type. False
   *  means the response is the inferred default (B2B Domestic) and
   *  the UI should render a "you haven't customised yet" state. */
  has_saved_state: boolean;
}

export interface ApplyResponse {
  success: boolean;
  doc_type: string;
  sub_doc_type: string | null;
  profile: WizardProfileId;
  answers: Record<string, string | string[]>;
  resolved: ResolvedPolicy;
}

// ── Service ───────────────────────────────────────────────────────

export default class PolicyWizardApiService extends AbstractApiService {
  public constructor() {
    super({});
  }

  /** Static schema (5 profiles + 13 questions). Boot-time call;
   *  identical across orgs — can be cached on the client side. */
  public async getDefinitions(): Promise<DefinitionsResponse> {
    return super.get(api.einvoice_wizard_definitions);
  }

  /** Currently-saved profile + answers + resolved policy for one
   *  (org × doc_type). Returns default-fallback shape when the
   *  wizard has never been saved on this doc-type. */
  public async getState(
    docType: string,
    subDocType?: string | null,
  ): Promise<StateResponse> {
    const params = new URLSearchParams({ doc_type: docType });
    if (subDocType) params.set('sub_doc_type', subDocType);
    return super.get(`${api.einvoice_wizard_state}?${params.toString()}`);
  }

  /** Persist a wizard run. Backend writes 8 attributes + replaces the
   *  rule-override set + invalidates the document_attributes Redis
   *  cache. Returns the resolved policy so the frontend can refresh
   *  its review step without a second roundtrip.
   *
   *  `applyCustomAnswers=true` means: the consultant picked a
   *  non-Custom profile but clicked "Customize answers anyway" —
   *  the resolver then overlays Tier-2 answers on the profile
   *  baseline instead of using profile defaults verbatim. */
  public async apply(
    profile: WizardProfileId,
    answers: Record<string, string | string[]>,
    docType: string,
    subDocType?: string | null,
    applyCustomAnswers = false,
  ): Promise<ApplyResponse> {
    const formData = new FormData();
    formData.append('profile', profile);
    formData.append('answers_json', JSON.stringify(answers));
    formData.append('doc_type', docType);
    if (subDocType) formData.append('sub_doc_type', subDocType);
    formData.append(
      'apply_custom_answers',
      applyCustomAnswers ? 'true' : 'false',
    );
    return super.post(api.einvoice_wizard_apply, formData);
  }
}
